import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { io } from 'socket.io-client'
import { limparCacheGet } from 'utils/carregamentoGlobal'
import { message } from 'antd'
import { 
  listNotifications, 
  markNotificationAsRead,
  deleteNotification
} from 'services/notifications'
import { filterNotificationsByRole, getUserRoleValue, sortNotificationsByNewest } from 'utils/rolePermissions'
import { getUserCachedData } from 'utils/UserAuthorization'
import { config } from 'config/environment'

const NotificationContext = createContext()

// Várias notificações juntas (ex.: robô) viram um aviso só, com um som só
const JANELA_AVISO_MS = 3000
let loteAvisos = []
let timerAvisos = null
function avisarEmLote(notification) {
  loteAvisos.push(notification)
  if (timerAvisos) return
  timerAvisos = setTimeout(() => {
    const lote = loteAvisos
    loteAvisos = []
    timerAvisos = null
    if (!lote.length) return
    if (lote.length === 1) {
      const n = lote[0]
      const nivel = n.level || n.type
      const fn = nivel === 'success' ? message.success : nivel === 'error' ? message.error : nivel === 'warning' ? message.warning : message.info
      fn(n.title)
    } else {
      message.info(`${lote.length} novas notificações — veja no sino`)
    }
    try {
      const audio = new Audio('/notification.mp3')
      audio.play().catch(() => {})
    } catch (err) {
      // sem áudio
    }
  }, JANELA_AVISO_MS)
}

// Singleton global para garantir apenas uma instância do socket
let globalSocket = null
const MAX_RECENT_NOTIFICATIONS = 30
const PUBLICATIONS_BADGE_PREFIX = 'publications:unread:v2'
const PUBLICATIONS_BADGE_COUNT_PREFIX = 'publications:unread-count:v2'
const PUBLICATIONS_KEYS_PREFIX = 'publications:unread-keys:v2'

function getPublicationBadgeScope() {
  const user = getUserCachedData() || {}
  const userPart = user?.id || user?.userId || user?.email || user?.username || 'default-user'
  const companyPart = user?.company?.id || user?.companyId || user?.company?.name || 'no-company'
  return `${companyPart}:${userPart}`
}

function getPublicationBadgeKey() {
  return `${PUBLICATIONS_BADGE_PREFIX}:${getPublicationBadgeScope()}`
}

function getPublicationBadgeCountKey() {
  return `${PUBLICATIONS_BADGE_COUNT_PREFIX}:${getPublicationBadgeScope()}`
}

function getPublicationKeysKey() {
  return `${PUBLICATIONS_KEYS_PREFIX}:${getPublicationBadgeScope()}`
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de um NotificationProvider')
  }
  return context
}

export const NotificationProvider = ({ children, enabled = false }) => {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [publicationUnreadCount, setPublicationUnreadCount] = useState(() => {
    try {
      const storedKeysRaw = localStorage.getItem(getPublicationKeysKey())
      if (storedKeysRaw) {
        const parsed = JSON.parse(storedKeysRaw)
        return Array.isArray(parsed) ? parsed.length : 0
      }
      const storedCount = Number(localStorage.getItem(getPublicationBadgeCountKey()))
      return Number.isFinite(storedCount) && storedCount > 0 ? storedCount : 0
    } catch (err) {
      return 0
    }
  })
  const [loading, setLoading] = useState(false)
  const socketRef = useRef(null)
  const addPublicationKeyRef = useRef(null)

  const applyRoleFilterAndSort = useCallback((items, userRole) => {
    const filteredItems = filterNotificationsByRole(items || [], userRole)
    return sortNotificationsByNewest(filteredItems).slice(0, MAX_RECENT_NOTIFICATIONS)
  }, [])

  // Conexão com socket.io para notificações em tempo real
  useEffect(() => {
    if (!enabled) return

    // Não conectar socket se estiver em rotas do portal do cliente
    if (window.location.pathname.startsWith('/portal-cliente')) {
      return;
    }

    // Obter token do cache de usuário (utilitário central)
    const userData = getUserCachedData()
    const token = userData?.token
    
    if (!token) {
      console.log('❌ Token não encontrado, socket não será conectado')
      return
    }

    // Reutiliza conexões existentes quando possível
    if (globalSocket && globalSocket.connected) {
      socketRef.current = globalSocket
      return
    }

    if (socketRef.current && socketRef.current.connected) {
      return
    }

    // Conectar ao socket com autenticação
    const newSocket = io(config.BACKEND_URL, {
        auth: {
          token: token // Envia token JWT para autenticação
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        forceNew: false // Reutilizar conexão existente se possível
    })

    socketRef.current = newSocket
    globalSocket = newSocket // Armazena globalmente

    // Listener de conexão
    newSocket.on('connect', () => {
      // connected
    })

    // Listener de autenticação confirmada
    newSocket.on('authenticated', (data) => {
      // authenticated
    })

    // Listener de nova notificação
    newSocket.on('new-notification', (notification) => {
      // chegou algo novo: as telas guardadas podem estar desatualizadas
      limparCacheGet()
      
      // Verificar se o usuário pode receber essa notificação
      const currentUser = getUserCachedData()
      const userRole = getUserRoleValue(currentUser)
      const filteredNotif = applyRoleFilterAndSort([notification], userRole)
      
      // Adiciona a notificação recebida em tempo real (se passar no filtro)
      if (filteredNotif.length > 0) {
        setNotifications(prev => {
          const merged = [filteredNotif[0], ...prev]
          return applyRoleFilterAndSort(merged, userRole)
        })
        
        avisarEmLote(notification)
      }
      // Se a notificação refere-se a uma publicação, marca badge persistente
      try {
        const isPublication = notification?.type === 'publication' || notification?.meta?.publicationId || notification?.category === 'publication'
        if (isPublication) {
            // calcula uma chave única para a publicação
            const pubKey = String(notification?.meta?.publicationId || notification?.meta?.movementId || notification?.movementId || notification?.id || notification?.uuid || Date.now())
            try { addPublicationKeyRef.current?.(pubKey) } catch (e) {}
        }
      } catch (err) {
        // ignore
      }
    })

    // Listener de erro de conexão
    newSocket.on('connect_error', (error) => {
      console.error('❌ Erro ao conectar socket:', error.message)
      if (error.message === 'Autenticação necessária' || error.message === 'Token inválido') {
        message.error('Erro de autenticação. Por favor, faça login novamente.')
      }
    })

    // Listener de desconexão
    newSocket.on('disconnect', (reason) => {
      // disconnected
    })

    // A sessão controla o ciclo de vida do socket. Ao sair, não deixamos
    // conexões autenticadas vivas nem reutilizáveis por outra sessão.
    return () => {
      if (socketRef.current) {
        socketRef.current.off('connect')
        socketRef.current.off('authenticated')
        socketRef.current.off('new-notification')
        socketRef.current.off('connect_error')
        socketRef.current.off('disconnect')
        socketRef.current.disconnect()
        if (globalSocket === socketRef.current) globalSocket = null
        socketRef.current = null
      }
    }
  }, [applyRoleFilterAndSort, enabled])

  // Listener para evento de logout
  useEffect(() => {
    const handleLogout = () => {
      if (globalSocket) {
        globalSocket.disconnect()
        globalSocket = null
      }
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }

    window.addEventListener('logout', handleLogout)

    return () => {
      window.removeEventListener('logout', handleLogout)
    }
  }, [])

  // Sempre que notifications mudar, recalcule o unreadCount
  useEffect(() => {
    const unreadNotifications = notifications.filter(n => !n.isRead && n.isRead !== undefined);
    setUnreadCount(unreadNotifications.length);
  }, [notifications])

  // ...restante do código permanece igual...

  // Carrega notificações do backend
  const loadNotifications = useCallback(async () => {
    if (!enabled || !getUserCachedData()?.token) {
      setNotifications([])
      return
    }

    try {
      setLoading(true)
      const response = await listNotifications({ limit: MAX_RECENT_NOTIFICATIONS })
      
      if (response?.data) {
        // Filtrar notificações baseado no role do usuário
        const currentUser = getUserCachedData()
        const userRole = getUserRoleValue(currentUser)
        const filteredNotifications = applyRoleFilterAndSort(response.data, userRole)
        
        setNotifications(filteredNotifications)
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error)
      // Mantém dados de exemplo em caso de erro
      setNotifications([
        {
          id: 1,
          title: 'Sistema de notificações ativo!',
          description: 'O sistema de push notifications está funcionando.',
          level: 'success',
          isRead: false,
          createdAt: new Date().toISOString(),
        }
      ])
    } finally {
      setLoading(false)
    }
  }, [applyRoleFilterAndSort, enabled])

  // Carrega dados iniciais
  useEffect(() => {
    if (enabled) {
      loadNotifications()
    } else {
      setNotifications([])
      setUnreadCount(0)
    }
  }, [enabled, loadNotifications])

  // Remover loadUnreadCount pois agora calculamos localmente

  // Adicionar nova notificação
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now() + Math.random(),
      date: new Date().toLocaleString('pt-BR'),
      read: false,
      ...notification
    }

    setNotifications(prev => [newNotification, ...prev])
    setUnreadCount(prev => prev + 1)

    // Mostrar toast baseado no tipo
    switch (notification.type) {
      case 'success':
        message.success(notification.title)
        break
      case 'error':
        message.error(notification.title)
        break
      case 'warning':
        message.warning(notification.title)
        break
      default:
        message.info(notification.title)
    }

    return newNotification.id
  }, [])

  // Marcar como lida
  const markAsRead = useCallback(async (id) => {
    try {
      // Atualiza no backend
      await markNotificationAsRead(id)
      
      // Atualiza localmente
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, isRead: true }
            : notification
        )
      )
      
      setUnreadCount(prev => Math.max(0, prev - 1))
      
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error)
      throw error // Propaga o erro para que a página possa lidar
    }
  }, [])

  const clearPublicationBadge = useCallback(() => {
    setPublicationUnreadCount(0)
    try {
      localStorage.removeItem(getPublicationKeysKey())
      localStorage.removeItem(getPublicationBadgeCountKey())
      localStorage.removeItem(getPublicationBadgeKey())
    } catch (err) {}
  }, [])

  const incrementPublicationBadge = useCallback((count) => {
    // suporte legado: aceita number (atualiza apenas contador de fallback)
    try {
      const prevRaw = localStorage.getItem(getPublicationKeysKey())
      const hasKeys = prevRaw && JSON.parse(prevRaw).length > 0
      if (hasKeys) return
    } catch (e) {}

    setPublicationUnreadCount((prev) => {
      const nextCount = Number.isFinite(Number(count)) ? Math.max(0, Number(count)) : prev + 1
      try { localStorage.setItem(getPublicationBadgeCountKey(), String(nextCount)) } catch (err) {}
      try {
        if (nextCount > 0) {
          localStorage.setItem(getPublicationBadgeKey(), '1')
        } else {
          localStorage.removeItem(getPublicationBadgeKey())
        }
      } catch (err) {}
      return nextCount
    })
  }, [])

  // Adiciona uma chave de publicação única ao set persistido
  const addPublicationKey = useCallback((key) => {
    if (!key) return
    try {
      const raw = localStorage.getItem(getPublicationKeysKey())
      const arr = raw ? JSON.parse(raw) : []
      const set = new Set(Array.isArray(arr) ? arr : [])
      if (!set.has(key)) {
        set.add(String(key))
        const list = Array.from(set)
        localStorage.setItem(getPublicationKeysKey(), JSON.stringify(list))
        setPublicationUnreadCount(list.length)
        try { localStorage.setItem(getPublicationBadgeKey(), '1') } catch (e) {}
      }
    } catch (err) {
      // fallback increment
      incrementPublicationBadge(1)
    }
  }, [incrementPublicationBadge])

  useEffect(() => {
    addPublicationKeyRef.current = addPublicationKey
  }, [addPublicationKey])

  // Adiciona múltiplas chaves (bulk)
  const addPublicationKeys = useCallback((keys = []) => {
    if (!Array.isArray(keys) || keys.length === 0) return
    try {
      const raw = localStorage.getItem(getPublicationKeysKey())
      const arr = raw ? JSON.parse(raw) : []
      const set = new Set(Array.isArray(arr) ? arr : [])
      keys.forEach(k => { if (k) set.add(String(k)) })
      const list = Array.from(set)
      localStorage.setItem(getPublicationKeysKey(), JSON.stringify(list))
      setPublicationUnreadCount(list.length)
      try { localStorage.setItem(getPublicationBadgeKey(), '1') } catch (e) {}
    } catch (err) {
      // fallback: set count
      incrementPublicationBadge(keys.length)
    }
  }, [incrementPublicationBadge])

  // Marcar todas como lidas
  const markAllAsRead = useCallback(async () => {
    try {
      // Para cada notificação não lida, marca como lida no backend
      const unreadNotifications = notifications.filter(n => !n.isRead)
      
      for (const notification of unreadNotifications) {
        await markNotificationAsRead(notification.id)
      }
      
      // Atualiza localmente
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      )
      setUnreadCount(0)
      
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error)
      throw error // Propaga o erro para que a página possa lidar
    }
  }, [notifications])

    // Remover notificação (deleta do backend)
  const removeNotification = useCallback(async (id) => {
    try {
      // Remove do backend
      await deleteNotification(id)
      
      // Remove localmente
      setNotifications(prev => {
        const notification = prev.find(n => n.id === id)
        if (notification && !notification.isRead) {
          setUnreadCount(prevCount => Math.max(0, prevCount - 1))
        }
        return prev.filter(n => n.id !== id)
      })
      
    } catch (error) {
      console.error('Erro ao deletar notificação:', error)
      throw error // Propaga o erro para que a página possa lidar
    }
  }, [])

  // Recarregar notificações
  const refreshNotifications = useCallback(() => {
    loadNotifications()
    // Remover loadUnreadCount pois calculamos localmente
  }, [loadNotifications])

  // Push notification helpers
  const pushSuccess = useCallback((title, description) => {
    return addNotification({ title, description, type: 'success' })
  }, [addNotification])

  const pushError = useCallback((title, description) => {
    return addNotification({ title, description, type: 'error' })
  }, [addNotification])

  const pushWarning = useCallback((title, description) => {
    return addNotification({ title, description, type: 'warning' })
  }, [addNotification])

  const pushInfo = useCallback((title, description) => {
    return addNotification({ title, description, type: 'info' })
  }, [addNotification])

  // Função para desconectar o socket (útil para logout)
  const disconnectSocket = useCallback(() => {
    // disconnect helper
    if (globalSocket) {
      globalSocket.disconnect()
      globalSocket = null
    }
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
    }
  }, [])

  const value = {
    notifications,
    unreadCount,
    hasUnreadPublications: publicationUnreadCount > 0,
    publicationUnreadCount,
    loading,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    refreshNotifications,
    disconnectSocket,
    clearPublicationBadge,
    incrementPublicationBadge,
    addPublicationKey,
    addPublicationKeys,
    // Push notification helpers
    pushSuccess,
    pushError,
    pushWarning,
    pushInfo
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export default NotificationContext
